import type { Messages } from '../i18n/messages';

/**
 * Actions module message catalog (Task 7e of the Fun Mode string sweep).
 * Every static chrome string in packages/app/src/modules/actions/* is keyed
 * here. Template/scaffolder-supplied data (workflow titles/descriptions, run
 * ids, step names, log lines, output text/links, entity refs) is never keyed
 * — only interpolated via `{param}` where needed.
 */
export const actionsMessages: Messages = {
  // ---- ActionsView.tsx — page header + tabs ----
  'actions.view.title': { en: 'Actions', fun: 'Button Mashing' },
  'actions.view.description': {
    en: 'Self-service workflows and the runs they kick off.',
    fun: 'Self-service workflows and the chaos they kick off.',
  },
  'actions.view.tabsAriaLabel': {
    en: 'Action sections',
    fun: 'Action sections',
  },
  'actions.view.tabWorkflows': { en: 'Workflows', fun: 'Workflows' },
  'actions.view.tabRuns': { en: 'Runs', fun: 'Runs' },
  'actions.view.loading': { en: 'Loading…', fun: 'Cooking…' },
  'actions.view.retentionNotice': {
    en: 'Runs are automatically removed {days} days after they finish.',
    fun: 'Runs get swept away {days} days after they wrap.',
  },

  // ---- Workflows tab filter bar ----
  'actions.workflowsFilter.searchAriaLabel': {
    en: 'Search workflows by name',
    fun: 'Search workflows by name',
  },
  'actions.workflowsFilter.searchPlaceholder': {
    en: 'Search name…',
    fun: 'Hunt for a name…',
  },
  'actions.workflowsFilter.tagLabel': { en: 'Tag', fun: 'Tag' },
  'actions.workflowsFilter.tagPlaceholder': { en: 'All tags', fun: 'Any tag' },
  'actions.workflowsFilter.ownerLabel': { en: 'Owner', fun: 'Owner' },
  'actions.workflowsFilter.ownerPlaceholder': {
    en: 'All owners',
    fun: 'Anyone',
  },

  // ---- Workflows tab empty states ----
  'actions.workflowsEmpty.title': {
    en: 'No workflows',
    fun: 'No workflows yet',
  },
  'actions.workflowsEmpty.description': {
    en: "Self-service workflows will appear here once they're registered in the catalog.",
    fun: "Self-service workflows will show up here once they're registered in the catalog.",
  },
  'actions.workflowsEmptyFiltered.title': {
    en: 'No matching workflows',
    fun: 'No workflows match that',
  },
  'actions.workflowsEmptyFiltered.description': {
    en: 'No workflows match these filters.',
    fun: 'Nothing matches these filters — try loosening up.',
  },

  // ---- WorkflowCard ----
  'actions.workflowCard.runLabel': { en: 'Run', fun: 'Go' },

  // ---- Runs table ----
  'actions.runsTable.columnWorkflow': { en: 'Workflow', fun: 'Workflow' },
  'actions.runsTable.columnRun': { en: 'Run', fun: 'Run' },
  'actions.runsTable.columnStatus': { en: 'Status', fun: 'Status' },
  'actions.runsTable.columnRunBy': { en: 'Run by', fun: 'Run by' },
  'actions.runsTable.columnCreated': { en: 'Created', fun: 'Created' },
  'actions.runsTable.unarchiveTooltip': {
    en: 'Unarchive run',
    fun: 'Bring it back',
  },
  'actions.runsTable.archiveTooltip': { en: 'Archive run', fun: 'Bury it' },

  // ---- Runs tab empty states ----
  'actions.runsEmpty.titleArchived': {
    en: 'No archived runs',
    fun: 'Nothing buried yet',
  },
  'actions.runsEmpty.title': { en: 'No runs yet', fun: 'No runs yet' },
  'actions.runsEmpty.description': {
    en: 'When you run a workflow, its run shows up here.',
    fun: 'Run a workflow and it shows up right here.',
  },
  'actions.runsEmpty.descriptionArchived': {
    en: 'Runs you archive will show up here.',
    fun: 'Bury a run and it shows up here.',
  },
  'actions.runsEmpty.descriptionFiltered': {
    en: 'No runs match these filters.',
    fun: 'Nothing matches these filters — try loosening up.',
  },

  // ---- Runs tab filter bar ----
  'actions.runsFilter.viewAriaLabel': { en: 'Run view', fun: 'Run view' },
  'actions.runsFilter.active': { en: 'Active', fun: 'Active' },
  'actions.runsFilter.archived': { en: 'Archived', fun: 'Archived' },
  'actions.runsFilter.workflowLabel': { en: 'Workflow', fun: 'Workflow' },
  'actions.runsFilter.workflowPlaceholder': {
    en: 'All workflows',
    fun: 'Any workflow',
  },
  'actions.runsFilter.statusLabel': { en: 'Status', fun: 'Status' },
  'actions.runsFilter.statusPlaceholder': {
    en: 'All statuses',
    fun: 'Any status',
  },
  'actions.runsFilter.createdLabel': { en: 'Created', fun: 'Created' },
  'actions.runsFilter.runByMe': { en: 'Run by me', fun: 'My handiwork' },

  // ---- Run status option labels (RUN_STATUS_OPTIONS) ----
  'actions.status.open': { en: 'Open', fun: 'Open' },
  'actions.status.processing': { en: 'Processing', fun: 'Cooking' },
  'actions.status.completed': { en: 'Completed', fun: 'Nailed it' },
  'actions.status.failed': { en: 'Failed', fun: 'Faceplanted' },
  'actions.status.cancelled': { en: 'Cancelled', fun: 'Called off' },
  'actions.status.skipped': { en: 'Skipped', fun: 'Skipped' },

  // ---- RunDetailView.tsx ----
  'actions.runDetail.backLink': { en: 'Back to runs', fun: 'Back to runs' },
  'actions.runDetail.cancelButton': { en: 'Cancel', fun: 'Pull the plug' },
  'actions.runDetail.runAgainButton': {
    en: 'Run again',
    fun: 'Go again',
  },
  'actions.runDetail.loadErrorTitle': {
    en: "Couldn't load this run",
    fun: 'This run went missing',
  },
  'actions.runDetail.loading': { en: 'Loading run…', fun: 'Rolling the dice…' },
  'actions.runDetail.noSteps': {
    en: 'No steps to show.',
    fun: 'Nothing to see here.',
  },
  'actions.runDetail.waitingForOutput': {
    en: 'Waiting for output…',
    fun: 'Waiting for the good stuff…',
  },
  'actions.runDetail.noLogs': {
    en: 'No logs for this step.',
    fun: 'Radio silence on this step.',
  },
  'actions.runDetail.outputTitle': { en: 'Output', fun: 'Goods' },
  'actions.runDetail.fallbackTitle': { en: 'Run', fun: 'Run' },

  // ---- ActionsPage.tsx — archive/unarchive snackbars ----
  'actions.page.archiveSuccess': { en: 'Run archived', fun: 'Run buried' },
  'actions.page.archiveError': {
    en: 'Could not archive run',
    fun: 'Could not bury that run',
  },
  'actions.page.unarchiveSuccess': {
    en: 'Run unarchived',
    fun: 'Run dug back up',
  },
  'actions.page.unarchiveError': {
    en: 'Could not unarchive run',
    fun: 'Could not dig that run back up',
  },

  // ---- WorkflowWizardPage.tsx ----
  'actions.wizard.backLink': { en: 'Back to actions', fun: 'Back to actions' },
  'actions.wizard.startError': {
    en: 'Could not start the workflow',
    fun: 'That workflow refused to start',
  },
};
